import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const DEFAULT_VALUE = {
  isPreview: false,
  slots: {},
  folders: [],
  setSlot: null,
  savePage: null,
};

const PreviewModeContext = createContext(DEFAULT_VALUE);

export function usePreviewMode() {
  return useContext(PreviewModeContext);
}

/**
 * Wraps official (non-edit) pages: fetches saved content overrides
 * and exposes them read-only. Renders children immediately with
 * defaults while loading.
 */
export function PageContentProvider({ pageId, children }) {
  const [slots, setSlots] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/content/${encodeURIComponent(pageId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data && data.slots && typeof data.slots === "object") {
          setSlots(data.slots);
        }
      })
      .catch(() => {
        // keep defaults on failure
      });
    return () => {
      cancelled = true;
    };
  }, [pageId]);

  const value = useMemo(
    () => ({ isPreview: false, slots, folders: [], setSlot: null, savePage: null }),
    [slots]
  );

  return (
    <PreviewModeContext.Provider value={value}>
      {children}
    </PreviewModeContext.Provider>
  );
}

/**
 * Wraps pages rendered inside the /edit/preview shell.
 * State (slots) lives in PreviewShell so it can save.
 */
export function PreviewProvider({ slots, setSlot, folders, savePage, children }) {
  const value = useMemo(
    () => ({ isPreview: true, slots, setSlot, folders, savePage }),
    [slots, setSlot, folders, savePage]
  );

  return (
    <PreviewModeContext.Provider value={value}>
      {children}
    </PreviewModeContext.Provider>
  );
}

export default PreviewModeContext;
